import { RadioGroup } from '@tvg/fuse/radio-group';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { EUserAction } from 'client/config';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import { DEFAULT_PRODUCTS_PER_PAGE, MAX_PRODUCTS_PER_PAGE } from 'common/config';
import React, { useCallback, useMemo } from 'react';
import styles from './ProductsPerPage.module.css';
import Radio from './Radio';

type ProductsPerPageProps = {
  position: string;
};

/* Returns a list of the products-per-page values (0 or 2) to be displayed */
export function getProductsPerPage(resultCount: number | undefined): string[] {
  const productsPerPage = [];
  if (resultCount !== undefined) {
    if (resultCount > Number(DEFAULT_PRODUCTS_PER_PAGE)) {
      productsPerPage.push(DEFAULT_PRODUCTS_PER_PAGE.toString());
      if (resultCount <= Number(MAX_PRODUCTS_PER_PAGE)) {
        productsPerPage.push('All');
      } else {
        productsPerPage.push(MAX_PRODUCTS_PER_PAGE.toString());
      }
    }
  }
  return productsPerPage;
}

function ProductsPerPage({ position }: ProductsPerPageProps) {
  const { data } = usePLPQuery(
    useCallback((d: PLPQueryData) => ({ resultsPerPage: d.resultsPerPage, numResults: d.numResults }), []),
  );
  const plpNavigate = usePLPNavigate();

  const resultsPerPage = data?.resultsPerPage ?? DEFAULT_PRODUCTS_PER_PAGE;

  const options = useMemo(() => {
    const numResults = data?.numResults ?? 0;
    if (numResults <= DEFAULT_PRODUCTS_PER_PAGE) return [];

    return [
      { value: DEFAULT_PRODUCTS_PER_PAGE, label: DEFAULT_PRODUCTS_PER_PAGE.toString() },
      {
        value: MAX_PRODUCTS_PER_PAGE,
        label: numResults <= MAX_PRODUCTS_PER_PAGE ? 'All' : MAX_PRODUCTS_PER_PAGE.toString(),
      },
    ];
  }, [data?.numResults]);

  const groupName = position === 'top' ? 'top-products-per-page' : 'bottom-products-per-page';

  const onChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) =>
      plpNavigate({ query: { numProducts: Number(e.currentTarget.value) } }, EUserAction.RESULT_COUNT),
    [plpNavigate],
  );

  return (
    <>
      {position === 'top' && options.length > 0 && <div className={styles.productsPerPage__separator} />}

      {options.length > 0 && (
        <RadioGroup
          stacked={false}
          legend="Number of results per page"
          groupName={groupName}
          className={styles.productsPerPage__radioGroup}
        >
          {options.map(({ value, label }) => (
            <Radio
              key={value}
              value={value}
              label={label}
              onChange={onChange}
              position={position}
              checked={value === resultsPerPage}
            />
          ))}
        </RadioGroup>
      )}
    </>
  );
}

export default React.memo(ProductsPerPage);
